import { APP_BASE_HREF, DOCUMENT } from '@angular/common';
import { Inject, ModuleWithProviders, NgModule, Optional, SkipSelf } from '@angular/core';

import { HubInfoConfig } from './../api';
import { isNil, isNotNil, perfMeasure } from './../utils';
import { WchNgComponentsModule } from './modules/components.module';
import { WchNgServicesModule } from './modules/services.module';
import { DependencyService } from './services/dependency/dependency.service';
import { SDK_MESSAGE_HANDLER } from './services/dependency/message';
import { SdkNavigateByPathHandler } from './services/dependency/message/sdk.navigate.by.path.message';
import { SdkRefreshHandler } from './services/dependency/message/sdk.refresh.message';
import { SdkSetModeHandler } from './services/dependency/message/sdk.set.mode.message';
import { SdkSubscribeActiveRouteHandler } from './services/dependency/message/sdk.subscribe.active.route.message';
import { SdkUnsubscribeHandler } from './services/dependency/message/sdk.subscribe.message';
import { SdkSubscribeModeHandler } from './services/dependency/message/sdk.subscribe.mode.message';
import { SdkSubscribeRouteHandler } from './services/dependency/message/sdk.subscribe.route.message';
import { HttpServiceOnHttpClient } from './services/http/http.service.on.http.client';
import { HttpServiceOnJsonp } from './services/http/http.service.on.jsonp';
import {
  selectApiURL,
  selectBaseURL,
  selectCycleHandlingStrategy,
  selectDeliveryURL,
  selectFetchLevels,
  selectHttpOptions,
  selectHttpPreviewOptions,
} from './services/hub-info/hub-info.service';
import {
  WCH_HUB_INFO_CONFIG,
  WCH_TOKEN_API_URL,
  WCH_TOKEN_BASE_URL,
  WCH_TOKEN_CYCLE_HANDLING_STRATEGY,
  WCH_TOKEN_DELIVERY_URL,
  WCH_TOKEN_FETCH_LEVELS,
  WCH_TOKEN_HTTP_OPTIONS,
  WCH_TOKEN_HTTP_PREVIEW_OPTIONS,
} from './services/hub-info/tokens';
import { LoggerProxy } from './services/logger/logger.service';
import { HTTP_SERVICE_ON_HTTP_CLIENT } from './utils/http.service.on.http.client';
import { HTTP_SERVICE_ON_JSONP } from './utils/http.service.on.jsonp';
import { wchGetAppBaseHref } from './utils/wch.utils';

/* Copyright IBM Corp. 2017 */
const LOGGER = 'WchNgModule';

@NgModule({
  exports: [WchNgServicesModule, WchNgComponentsModule]
})
export class WchNgModule {
  /*
   * Returns the preconfigured module
   *
   * @param aService  the service
   */
  static forRoot(aService?: HubInfoConfig): ModuleWithProviders {
    return {
      ngModule: WchNgModule,
      providers: [
        { provide: WCH_HUB_INFO_CONFIG, useValue: aService },
        {
          provide: WCH_TOKEN_API_URL,
          useFactory: selectApiURL,
          deps: [WCH_HUB_INFO_CONFIG]
        },
        {
          provide: WCH_TOKEN_DELIVERY_URL,
          useFactory: selectDeliveryURL,
          deps: [WCH_HUB_INFO_CONFIG]
        },
        {
          provide: WCH_TOKEN_BASE_URL,
          useFactory: selectBaseURL,
          deps: [WCH_HUB_INFO_CONFIG]
        },
        {
          provide: WCH_TOKEN_HTTP_OPTIONS,
          useFactory: selectHttpOptions,
          deps: [WCH_HUB_INFO_CONFIG]
        },
        {
          provide: WCH_TOKEN_HTTP_PREVIEW_OPTIONS,
          useFactory: selectHttpPreviewOptions,
          deps: [WCH_HUB_INFO_CONFIG]
        },
        {
          provide: WCH_TOKEN_CYCLE_HANDLING_STRATEGY,
          useFactory: selectCycleHandlingStrategy,
          deps: [WCH_HUB_INFO_CONFIG]
        },
        {
          provide: WCH_TOKEN_FETCH_LEVELS,
          useFactory: selectFetchLevels,
          deps: [WCH_HUB_INFO_CONFIG]
        },
        {
          provide: HTTP_SERVICE_ON_HTTP_CLIENT,
          useClass: HttpServiceOnHttpClient
        },
        { provide: HTTP_SERVICE_ON_JSONP, useClass: HttpServiceOnJsonp },
        {
          provide: SDK_MESSAGE_HANDLER,
          useClass: SdkRefreshHandler,
          multi: true
        },
        {
          provide: SDK_MESSAGE_HANDLER,
          useClass: SdkNavigateByPathHandler,
          multi: true
        },
        {
          provide: SDK_MESSAGE_HANDLER,
          useClass: SdkSubscribeActiveRouteHandler,
          multi: true
        },
        {
          provide: SDK_MESSAGE_HANDLER,
          useClass: SdkSubscribeModeHandler,
          multi: true
        },
        {
          provide: SDK_MESSAGE_HANDLER,
          useClass: SdkSetModeHandler,
          multi: true
        },
        {
          provide: SDK_MESSAGE_HANDLER,
          useClass: SdkUnsubscribeHandler,
          multi: true
        },
        {
          provide: SDK_MESSAGE_HANDLER,
          useClass: SdkSubscribeRouteHandler,
          multi: true
        },
        {
          provide: APP_BASE_HREF,
          useFactory: wchGetAppBaseHref,
          deps: [
            [new Optional(), new Inject(WCH_TOKEN_BASE_URL)],
            [new Optional(), DOCUMENT]
          ]
        }
      ]
    };
  }

  /*
   * This contructor prevents the module from being imported multiple times.
   *
   * @param parentModule
   */
  constructor(
    @Optional()
    @SkipSelf()
    parentModule: WchNgModule,
    loggerService: LoggerProxy,
    depService: DependencyService
  ) {
    // measure
    const handle = perfMeasure(LOGGER);
    // do not allow recursive includes
    if (isNotNil(parentModule)) {
      throw new Error(
        'WchNgModule is already loaded. Import it in the AppModule only'
      );
    }
    // make sure the logger exists
    if (isNil(loggerService)) {
      throw new Error('Logger Service required');
    }
    // make sure the logger exists
    if (isNil(depService)) {
      throw new Error('Dependency Service required');
    }
    // done
    handle();
  }
}
